/**
 * ArtifactPanel
 * Persistent right-side panel (Claude-artifacts style) showing agent-generated
 * outputs: live "generating" state on artifact_start, the rendered preview on
 * artifact_render, a version dropdown, and download (native, or HTML→PDF).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Dropdown, Popconfirm, Spin, Tooltip, message as antdMessage } from 'antd';
import { DownloadOutlined, DeleteOutlined } from '@ant-design/icons';
import { useLanguage } from '@/hooks/useLanguage';
import { useFeature } from '@hooks/useFeature';
import { createLogger } from '@/utils/logger';
import { SaveToDriveModal } from '../drive/SaveToDriveModal';
import { ExternalLinkIcon, GoogleDriveGlyph } from '../drive/driveIcons';
import { useDriveConnected } from '../../hooks/useDriveConnected';
import { safeHttpUrl } from '@/utils/safeHttpUrl';
import type { DriveCopy } from '@/types/models';
import artifactService, { extensionForFormat } from '../../services/artifactService';
import { OutputFormatIcon } from '@modules/chatbots/components/outputFormatIcons';
import type { Artifact } from '@/types/models';
import ArtifactViewer from './ArtifactViewer';
import ArtifactErrorBoundary from './ArtifactErrorBoundary';
import { artifactStatusSeq } from '../../utils/artifactStatus';
import { formatExactTime, formatRelativeTime } from '@/utils/relativeTime';
import './ArtifactPanel.css';

const logger = createLogger('ArtifactPanel');

interface Props {
  artifacts: Record<string, Artifact>;
  order: string[];
  activeId: string | null;
  onSelect: (id: string) => void;
  onViewVersion: (id: string, version: number) => void;
  onResize: (width: number) => void;
  onDeleteVersion?: (id: string, version: number) => Promise<void>;
}

/** Body-only panel hosted by the unified side-panel dock in ConversationPanel —
 *  the dock owns all chrome (tab strip, collapse rail, width). */
const ArtifactPanel: React.FC<Props> = ({
  artifacts, order, activeId, onSelect, onViewVersion, onResize, onDeleteVersion,
}) => {
  const { t } = useLanguage();
  const [downloading, setDownloading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement | null>(null);
  // Drive connector write path — same two tenant switches as the agent's
  // save_to_google_drive tool, so the button and the tool appear together.
  const cloudConnectionsOn = useFeature('integrations.cloud_storage');
  const cloudExportOn = useFeature('export.cloud_docs');
  // …and, like the composer, only for someone who actually connected Drive:
  // the header keeps exactly the controls it had for everyone else.
  const driveSaveEnabled = useDriveConnected(cloudConnectionsOn && cloudExportOn);
  const [saveToDriveOpen, setSaveToDriveOpen] = useState(false);
  // Copies saved during this session, until the artifact is re-fetched.
  const [sessionDriveCopies, setSessionDriveCopies] = useState<Record<string, DriveCopy[]>>({});
  // Close the artifact/version picker when clicking outside it.
  useEffect(() => {
    if (!pickerOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [pickerOpen]);
  const active = activeId ? artifacts[activeId] : null;

  // Cycle engaging status sentences while an artifact is being generated.
  const [statusIdx, setStatusIdx] = useState(0);
  const genActive = !!active && (active.status === 'streaming' || active.status === 'rendering');
  useEffect(() => {
    if (!genActive) { setStatusIdx(0); return; }
    const id = window.setInterval(() => setStatusIdx(i => i + 1), 2500);
    return () => window.clearInterval(id);
  }, [genActive, activeId]);

  const tabs = useMemo(
    () => order.map(id => ({ id, a: artifacts[id] })).filter(x => x.a),
    [order, artifacts],
  );

  // Drag-to-resize, mirroring the AoA split handle. RTL-aware.
  const onHandleDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const dockEl = (e.currentTarget as HTMLElement).closest('.side-panel-dock') as HTMLElement | null;
    const startW = dockEl ? dockEl.getBoundingClientRect().width : 460;
    const rtl = getComputedStyle(document.documentElement).direction === 'rtl';
    const onMove = (ev: PointerEvent) => {
      const dx = ev.clientX - startX;
      onResize(rtl ? startW + dx : startW - dx);
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.body.classList.remove('artifact-resizing');
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.body.classList.add('artifact-resizing');
  }, [onResize]);

  if (!active) return null;


  const isGenerating = active.status === 'streaming' || active.status === 'rendering';

  const handleDownload = async (asPdf = false) => {
    if (!active.artifact_id) return;
    setDownloading(true);
    try {
      const base = (active.title || 'artifact').replace(/[^\w֐-׿-]+/g, '-');
      if (asPdf) {
        await artifactService.downloadAsPdf(active.artifact_id, base);
      } else {
        await artifactService.download(active.artifact_id, `${base}.${extFor(active.output_format)}`);
      }
    } catch (err) {
      logger.error('Artifact download failed', err);
      antdMessage.error(t('chat.artifacts.downloadFailed'));
    } finally {
      setDownloading(false);
    }
  };

  const handleDeleteVersion = async (id: string, version: number) => {
    if (!id || !onDeleteVersion) return;
    setDeleting(true);
    try {
      await onDeleteVersion(id, version);
      antdMessage.success(t('chat.artifacts.deleted'));
    } catch (err) {
      logger.error('Artifact version delete failed', err);
      antdMessage.error(t('chat.artifacts.deleteFailed'));
    } finally {
      setDeleting(false);
    }
  };

  // Jump to an artifact (optionally a specific version) from the dropdown.
  const jumpTo = (id: string, version?: number | null) => {
    onSelect(id);
    if (version != null) onViewVersion(id, version);
    setPickerOpen(false);
  };

  // Only a FALLBACK: artifactService prefers the filename the server sent,
  // which is built from the renderer's own extension. This map exists for a
  // response with no Content-Disposition. Keep every producible format listed —
  // the bug this replaced was `xlsx` falling through to 'md', which downloaded
  // a real Excel file under a markdown name.
  const extFor = extensionForFormat;

  // Download a specific artifact (optionally a specific version) from the dropdown.
  const downloadOne = async (a: Artifact, version?: number) => {
    if (!a.artifact_id || a.output_format === 'gforms') return;
    setDownloading(true);
    try {
      const base = (a.title || 'artifact').replace(/[^\w֐-׿-]+/g, '-');
      const vlabel = version != null ? `-v${version}` : '';
      await artifactService.download(a.artifact_id, `${base}${vlabel}.${extFor(a.output_format)}`, version);
    } catch (err) {
      logger.error('Artifact download failed', err);
      antdMessage.error(t('chat.artifacts.downloadFailed'));
    } finally {
      setDownloading(false);
    }
  };

  const downloadMenu = active.output_format === 'html'
    ? {
        items: [
          { key: 'pdf', label: t('chat.artifacts.downloadPdf') },
          { key: 'html', label: t('chat.artifacts.downloadHtml') },
        ],
        onClick: ({ key }: { key: string }) => handleDownload(key === 'pdf'),
      }
    : undefined;

  return (
    <aside className="artifact-panel" aria-label={t('chat.artifacts.panelTitle')}>
      {/* Drag-to-resize handle on the inline-start edge (mirrors AoA split). */}
      <div
        className="artifact-panel__resize"
        role="separator"
        aria-orientation="vertical"
        onPointerDown={onHandleDown}
        title={t('chat.artifacts.resize')}
      >
        <span className="artifact-panel__resize-grip" aria-hidden="true" />
      </div>

      {/* Toolbar row — the artifact/version dropdown plus the download control.
          The artifact name, format and version live inside the dropdown. */}
      {tabs.length > 0 && (
        <div className="artifact-panel__picker" ref={pickerRef}>
          <div className="artifact-panel__picker-row">
          {(() => {
            const shownV = active.viewing?.version ?? active.current_version;
            const shownAt = (active.versions || []).find(v => v.version === shownV)?.at;
            return (
              <button
                type="button"
                className={`artifact-panel__picker-current ${pickerOpen ? 'is-open' : ''}`}
                onClick={() => setPickerOpen(o => !o)}
              >
                <span className="artifact-panel__picker-thumb">
                  {active.output_format && <OutputFormatIcon format={active.output_format} size={22} />}
                </span>
                <span className="artifact-panel__picker-text">
                  <span className="artifact-panel__picker-title" dir="auto">
                    {active.title || t('chat.artifacts.untitled')}
                  </span>
                  <span className="artifact-panel__picker-meta" title={shownAt ? formatExactTime(shownAt) : undefined}>
                    {(active.output_format || '').toUpperCase()}
                    {shownV != null ? ` · v${shownV}` : ''}
                    {shownAt ? ` · ${formatRelativeTime(shownAt)}` : ''}
                  </span>
                </span>
                <svg className="artifact-panel__picker-chevron" width="16" height="16"
                     viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                     strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>
              </button>
            );
          })()}
            <div className="artifact-panel__actions">
              {active.output_format !== 'gforms' && (
                downloadMenu ? (
                  <Dropdown menu={downloadMenu} trigger={['click']}>
                    <Button size="small" icon={<DownloadOutlined />} loading={downloading} />
                  </Dropdown>
                ) : (
                  <Tooltip title={t('chat.artifacts.download')}>
                    <Button
                      size="small"
                      icon={<DownloadOutlined />}
                      loading={downloading}
                      onClick={() => handleDownload(false)}
                    />
                  </Tooltip>
                )
              )}
              {driveSaveEnabled && active.output_format !== 'gforms' && (() => {
                const copies = [...(active.drive_copies || []), ...(sessionDriveCopies[active.artifact_id] || [])];
                const latest = copies.length ? copies[copies.length - 1] : null;
                const latestLink = safeHttpUrl(latest?.web_view_link);
                return (
                  <>
                    {latestLink && (
                      <Tooltip title={t('chat.drive.openInDrive')}>
                        <Button
                          size="small"
                          icon={<ExternalLinkIcon size={14} />}
                          href={latestLink}
                          target="_blank"
                          rel="noopener noreferrer"
                        />
                      </Tooltip>
                    )}
                    <Tooltip title={t('chat.drive.saveToDrive')}>
                      <Button
                        size="small"
                        className="artifact-panel__drive-btn"
                        icon={<GoogleDriveGlyph size={14} />}
                        onClick={() => setSaveToDriveOpen(true)}
                      />
                    </Tooltip>
                  </>
                );
              })()}
            </div>
          </div>

          {driveSaveEnabled && (
            <SaveToDriveModal
              open={saveToDriveOpen}
              artifactId={active.artifact_id}
              title={active.title || t('chat.artifacts.untitled')}
              format={active.output_format}
              version={active.viewing?.version}
              onClose={() => setSaveToDriveOpen(false)}
              onSaved={(file) => setSessionDriveCopies((prev) => ({
                ...prev,
                [active.artifact_id]: [...(prev[active.artifact_id] || []), file],
              }))}
            />
          )}

          {pickerOpen && (
            <div className="artifact-panel__picker-menu">
              {[...tabs].reverse().map(({ id, a }) => {
                const versions = [...(a.versions || [])].sort(
                  (x, y) => (y.version ?? 0) - (x.version ?? 0),
                );
                const rows = versions.length
                  ? versions
                  : [{ version: a.current_version, at: undefined } as any];
                return (
                  <div key={id} className="artifact-panel__picker-group">
                    {/* Group head is a BUTTON: click jumps straight to this
                        artifact's latest version. */}
                    <button
                      type="button"
                      className={`artifact-panel__picker-group-head ${id === activeId ? 'is-active' : ''}`}
                      onClick={() => jumpTo(id, a.current_version)}
                    >
                      {a.output_format && <OutputFormatIcon format={a.output_format} size={18} />}
                      <span className="artifact-panel__picker-group-title" dir="auto">
                        {a.title || t('chat.artifacts.untitled')}
                      </span>
                      <span className="artifact-panel__picker-group-count">
                        {t('chat.artifacts.versionsCount', { count: rows.length })}
                      </span>
                      <span className="artifact-panel__picker-group-fmt" dir="auto">
                        {(a.output_format || '').toUpperCase()}
                      </span>
                    </button>
                    {rows.map((v: any) => {
                      const isLatest = v.version === a.current_version;
                      const shownV = id === activeId
                        ? (active.viewing?.version ?? active.current_version)
                        : null;
                      const selected = id === activeId && shownV === v.version;
                      return (
                        <div key={v.version} className="artifact-panel__picker-vrow">
                          <button
                            type="button"
                            className={`artifact-panel__picker-item ${selected ? 'is-selected' : ''}`}
                            onClick={() => jumpTo(id, v.version)}
                          >
                            {/* Timeline marker: ✓ = the version on screen,
                                ● = latest, ○ = older version. */}
                            <span className="artifact-panel__picker-marker" aria-hidden="true">
                              {selected ? (
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                     strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M20 6L9 17l-5-5" />
                                </svg>
                              ) : (
                                <span className={`artifact-panel__picker-dot ${isLatest ? 'is-latest' : ''}`} />
                              )}
                            </span>
                            <span className="artifact-panel__picker-vlabel">
                              v{v.version}
                              {isLatest && (
                                <span className="artifact-panel__picker-latest">
                                  {t('chat.artifacts.latestTag')}
                                </span>
                              )}
                            </span>
                            {v.at && (
                              <span className="artifact-panel__picker-vtime" title={formatExactTime(v.at)}>
                                {formatRelativeTime(v.at)}
                              </span>
                            )}
                          </button>
                          {/* Per-version actions — revealed on row hover/focus
                              to keep the list scannable. */}
                          <span className="artifact-panel__picker-vactions">
                            {a.output_format !== 'gforms' && (
                              <Tooltip title={t('chat.artifacts.download')}>
                                <Button
                                  type="text"
                                  size="small"
                                  icon={<DownloadOutlined />}
                                  onClick={(e) => { e.stopPropagation(); void downloadOne(a, v.version); }}
                                />
                              </Tooltip>
                            )}
                            {onDeleteVersion && v.version != null && a.can_edit !== false && (
                              <Popconfirm
                                title={t('chat.artifacts.deleteVersionConfirm', { version: v.version })}
                                onConfirm={() => handleDeleteVersion(id, v.version)}
                                okText={t('common.yes')}
                                cancelText={t('common.no')}
                                okButtonProps={{ danger: true }}
                              >
                                <Tooltip title={t('chat.artifacts.deleteVersion')}>
                                  <Button
                                    type="text"
                                    size="small"
                                    danger
                                    loading={deleting}
                                    icon={<DeleteOutlined />}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </Tooltip>
                              </Popconfirm>
                            )}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className="artifact-panel__body">
        {isGenerating ? (
          <div className="artifact-panel__generating">
            <Spin />
            {(() => {
              const seq = artifactStatusSeq(t, active.output_format, active.op);
              return <span>{seq[statusIdx % seq.length]}</span>;
            })()}
          </div>
        ) : (
          <ArtifactErrorBoundary resetKey={`${activeId}:${active.viewing?.version ?? active.current_version ?? ''}`}>
            <ArtifactViewer artifact={active} />
          </ArtifactErrorBoundary>
        )}
      </div>
    </aside>
  );
};

export default ArtifactPanel;
